import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import CollectAndBuildImage from './CollectAndBuildImage'

const copy = {
  heading: `Collect and build customer journey analytics`,
}

const link = {
  text: `Explore Connections`,
  url: `/product/connections/`,
}

const list = [
  {
    title: `Collect`,
    description: `Complete your view of the customer journey by pulling together data across web, mobile web, apps, email and support interactions.`,
  },
  {
    title: `Understand`,
    description: `Identify the characteristics of your highest-value shoppers and create a targeted strategy.`,
  },
  {
    title: `Centralize`,
    description: `Centralize and scale access to high-quality, secure data.`,
  },
]

const CollectAndBuild: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <SectionBase variant="2Col">
        <SectionCopy>
          <CopyTextBase>
            <StyledCopyTextHeading tag="h2">{copy.heading}</StyledCopyTextHeading>
            <ColumnTrack>
              {list.map(({ title, description }, index) => (
                <FeatureBoxBase key={index} alignment="left">
                  <FeatureBox.Body>
                    <FeatureBox.Title>{title}</FeatureBox.Title>
                    <FeatureBox.Text>{description}</FeatureBox.Text>
                  </FeatureBox.Body>
                </FeatureBoxBase>
              ))}
            </ColumnTrack>
            <CopyText.Link href={getAbsoluteUrl(link.url, pageConfig)}>{link.text}</CopyText.Link>
          </CopyTextBase>
        </SectionCopy>

        <StyledSectionContents>
          <CollectAndBuildImage />
        </StyledSectionContents>
      </SectionBase>
    </Wrapper>
  )
}

const StyledSectionContents = styled(Section.Contents)`
  align-self: center;
`

const StyledCopyTextHeading = styled(CopyText.Heading)`
  margin-bottom: 40px;
`

const Wrapper = styled.div`
  display: grid;
  margin-top: -40px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-top: -80px;
  }
`

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 392px;
    justify-self: end;
    align-self: center;
  }
`

export default CollectAndBuild
